import {issueHovercardPath} from '@github-ui/paths'
import {type SafeHTMLString, SafeHTMLBox} from '@github-ui/safe-html'
import {Link, Text} from '@primer/react'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {graphql} from 'relay-runtime'
import type {StickyHeaderTitle$key} from './__generated__/StickyHeaderTitle.graphql'
import {useFragment} from 'react-relay'
import {useCallback} from 'react'

type StickyHeaderTitleProps = {
  headerTitleData: StickyHeaderTitle$key
  scrollToTopOnClick?: boolean
}

export function StickyHeaderTitle({headerTitleData, scrollToTopOnClick}: StickyHeaderTitleProps) {
  const {titleHTML, number, url, repository} = useFragment(
    graphql`
      fragment StickyHeaderTitle on Issue {
        titleHTML
        number
        url
        repository {
          name
          owner {
            login
          }
        }
      }
    `,
    headerTitleData,
  )

  const scrollToTop = useCallback(() => {
    if (!scrollToTopOnClick) {
      return
    }
    setTimeout(() => {
      // Clicking the link to #top will scroll to the top of the page, but it will also append "#top" to the URL.
      // We want to prevent that from happening, so we replace the URL right after the scroll happens.
      window.history.replaceState(history.state, '', url)
    }, 10)
  }, [scrollToTopOnClick, url])

  return (
    <>
      <Link
        data-hovercard-url={issueHovercardPath({
          owner: repository.owner.login,
          repo: repository.name,
          issueNumber: number,
        })}
        sx={{color: 'fg.default', fontWeight: 'bold', display: 'inline'}}
        href={scrollToTopOnClick ? '#top' : url}
        target={scrollToTopOnClick ? undefined : '_blank'}
        onClick={scrollToTop}
      >
        <SafeHTMLBox
          as="bdi"
          sx={{
            wordBreak: 'break-word',
            mr: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          className="markdown-title"
          html={titleHTML as SafeHTMLString}
          data-testid={TEST_IDS.issueTitle}
        />
      </Link>
      <Text
        sx={{
          color: 'fg.muted',
        }}
      >
        {LABELS.issueNumber(number)}
      </Text>
    </>
  )
}

try{ StickyHeaderTitle.displayName ||= 'StickyHeaderTitle' } catch {}